import { useMutation, useQueryClient, type QueryKey } from '@tanstack/react-query';
import { toast } from 'sonner';

export interface CandidateMutationParams {
  queryKey: QueryKey;
  isAnonymous: boolean;
  shareId: string | null;
}

interface FeedbackMessages {
  success: string;
  error: string;
}

export type CandidateMutation<TVariables> = ((variables: TVariables) => void) & {
  isPending: boolean;
};

/**
 * Wraps a candidate mutation with toast feedback and query invalidation. The query is
 * invalidated on success AND failure: a 409 usually means another tab changed the data,
 * so the stale view must refresh. Returns the `mutate` callback (with `isPending`
 * attached) so callers stay declarative.
 */
export function useCandidateMutation<TVariables>(
  queryKey: QueryKey,
  mutationFn: (variables: TVariables) => Promise<unknown>,
  messages: FeedbackMessages,
  alsoInvalidate?: QueryKey
): CandidateMutation<TVariables> {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn,
    onSuccess: () => toast.success(messages.success),
    onError: () => toast.error(messages.error),
    onSettled: () => {
      if (alsoInvalidate) void queryClient.invalidateQueries({ queryKey: alsoInvalidate });
      return queryClient.invalidateQueries({ queryKey });
    },
  });
  return Object.assign((variables: TVariables) => mutation.mutate(variables), {
    isPending: mutation.isPending,
  });
}
